import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Shield } from "lucide-react";
import { UserRole } from "@/state/session";
import { z } from "zod";

const updateUserSchema = z.object({
  name: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
  role: z.enum(["admin", "gestor", "funcionario"]),
});

interface UserEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: {
    id: string;
    name: string;
    email: string;
    role: UserRole;
  } | null;
  onSuccess: () => void;
}

const roleLabels: Record<UserRole, string> = {
  admin: "Administrador",
  gestor: "Gestor",
  funcionario: "Funcionário",
};

export function UserEditDialog({ open, onOpenChange, user, onSuccess }: UserEditDialogProps) {
  const { toast } = useToast();
  const [name, setName] = useState(user?.name || "");
  const [role, setRole] = useState<UserRole>(user?.role || "funcionario");
  const [isUpdating, setIsUpdating] = useState(false);

  // Update state when user prop changes
  useState(() => {
    if (user) {
      setName(user.name);
      setRole(user.role);
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsUpdating(true);

    try {
      // Validate data
      updateUserSchema.parse({ name, role });

      // Call edge function
      const { data, error } = await supabase.functions.invoke('update-user', {
        body: {
          userId: user.id,
          name,
          role,
        },
      });

      if (error) {
        throw error;
      }

      toast({
        title: "✅ Usuário Atualizado!",
        description: `${name} foi atualizado como ${roleLabels[role]}.`,
        duration: 4000,
      });

      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error('❌ Erro ao atualizar usuário:', error);

      // Handle validation errors
      if (error?.issues) {
        const validationErrors = error.issues.map((issue: any) => issue.message).join(', ');
        toast({
          title: "⚠️ Dados Inválidos",
          description: validationErrors,
          variant: "destructive",
          duration: 5000,
        });
      } else {
        toast({
          title: "❌ Erro ao Atualizar",
          description: error.message || "Não foi possível atualizar o usuário. Tente novamente.",
          variant: "destructive",
          duration: 5000,
        });
      }
    } finally {
      setIsUpdating(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Usuário</DialogTitle>
          <DialogDescription>
            Atualize as informações de {user.name}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Nome</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome completo"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-email">E-mail</Label>
            <Input
              id="edit-email"
              type="email"
              value={user.email}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              O e-mail não pode ser alterado
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-role">Função</Label>
            <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">
                  <span className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Administrador
                  </span>
                </SelectItem>
                <SelectItem value="gestor">Gestor</SelectItem>
                <SelectItem value="funcionario">Funcionário</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={isUpdating}
            >
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar Alterações"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
